import { prop, head, includes, compose, propOr } from 'ramda';
import { ALL_SUPPORTED_LANGUAGES, HPX_SUPPORTED_LANGUAGES, LEGACY_SUPPORTED_LANGUAGES_KEY, SUPPORTED_LANGUAGES } from '../constants/i18n';

export const getBrowserLocale = () => {
  const {
    language,
    browserLanguage,
    systemLanguage,
    userLanguage,
    languages = []
  } = window.navigator;
  return language || browserLanguage || systemLanguage || userLanguage || head(languages);
};

export const splitLocaleIntoCountryAndLanguage = (locale = '') => ({
  language: locale.slice(0, 2),
  country: locale.slice(-2, locale.length).toLowerCase()
});

/**
 * Determines the locale based on browser settings and supported languages.
 * 
 * @param {string} [supported_languages_key=LEGACY_SUPPORTED_LANGUAGES_KEY] - The key to access the supported languages object
 * @returns {Object} An object containing the country and language codes
 * @returns {string} returns.country - The country code
 * @returns {string} returns.language - The language code
 * 
 * @example
 * // Returns { language: 'en', country: 'gb' } if browser language is not supported
 * const locale = getLocale();
 * 
 * @example
 * // Returns { country: 'us', language: 'en' } for English browser with US region (if supported)
 * const locale = getLocale('CUSTOM_LANGUAGES_KEY');
 */
export const getLocale = (supported_languages_key = LEGACY_SUPPORTED_LANGUAGES_KEY) => {
  const browserLang = getBrowserLocale();
  const { country, language } = splitLocaleIntoCountryAndLanguage(browserLang);
  const supported_languages = propOr(
    SUPPORTED_LANGUAGES,
    supported_languages_key,
    ALL_SUPPORTED_LANGUAGES
  );
  if (language in supported_languages){
    return { country, language };
  } 
  else return { language: 'en', country: 'gb' };
};


/**
 * Determines the locale (language and country) based on the provided supported languages key.
 * 
 * @param {string} [supported_languages_key=LEGACY_SUPPORTED_LANGUAGES_KEY] - The key to look up supported languages in ALL_SUPPORTED_LANGUAGES.
 * @returns {Object} An object containing the determined language and country codes.
 * @returns {string} returns.language - The language code (e.g., 'en').
 * @returns {string} returns.country - The country code (e.g., 'gb').
 * 
 * If the detected language is supported, it returns that language with either:
 * - The detected country (if it's supported for that language)
 * - The first supported country for that language (if detected country isn't supported)
 * 
 * If the language isn't supported at all, it defaults to English (UK) - 'en'/'gb'.
 */
export function determineLocale(supported_languages_key = LEGACY_SUPPORTED_LANGUAGES_KEY) {
  const { country, language } = getLocale(supported_languages_key);
  const supported_languages = propOr(
    SUPPORTED_LANGUAGES,
    supported_languages_key,
    ALL_SUPPORTED_LANGUAGES
  );
  if (prop(language, supported_languages)) {
    return compose(includes(country), prop(language))(supported_languages)
      ? { language, country }
      : {
          language,
          country: compose(head, prop(language))(supported_languages)
        };
  }
  return { language: 'en', country: 'gb' };
}
